import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { Encounter, Patient, PatientIdentity } from '@pixacare/domain';
import { PatientReportRequest } from '@pixacare/dtos';
import { formatToDateOnly, PagedResponse, PaginationQuery } from '@pixacare/shared';
import { TeleexpertiseProvider } from '@shared/models/enums/teleexpertise-provider.enum';
import { map, Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { PdfResponse } from '@shared/models/pdf-response';

@Injectable({
  providedIn: 'root',
})
export class PatientHttpService {
  private readonly http = inject(HttpClient);
  private readonly sanitizer = inject(DomSanitizer);

  createPatient(clientCode: string, patient: PatientIdentity): Observable<Patient> {
    return this.http.post<Patient>(`${environment.apiBaseUrl}/patient/${clientCode}`, {
      ...patient,
      birthDate: formatToDateOnly(patient.birthDate),
    });
  }

  getPatients(
    clientCode: string,
    { query = new PaginationQuery({ orderBy: ['last_activity|desc'] }) } = {},
  ): Observable<PagedResponse<Patient>> {
    return this.http.get<PagedResponse<Patient>>(`${environment.apiBaseUrl}/patient/${clientCode}`, {
      headers: { 'X-version': '2.0' },
      params: { paginationOptions: String(query.encodedQuery || query.encode() || '') },
    });
  }

  getPatientsByBusinessIdentifier(clientCode: string, businessIdentifier: string): Observable<Patient[]> {
    return this.http.get<Patient[]>(
      `${environment.apiBaseUrl}/patient/${clientCode}?businessIdentifier=${businessIdentifier}`,
    );
  }

  getPatientEncounters(clientCode: string, patientId: number): Observable<Encounter[]> {
    return this.http.get<Encounter[]>(`${environment.apiBaseUrl}/patient/${patientId}/encounter`, {
      params: { clientCode },
    });
  }

  mergePatients(clientCode: string, patientId: number, toPatientId: number = null): Observable<unknown> {
    return this.http.delete(
      `${environment.apiBaseUrl}/patient/${clientCode}/${patientId}?toPatientId=${toPatientId || ''}`,
    );
  }

  deletePatient(clientCode: string, patientId: number): Observable<unknown> {
    return this.mergePatients(clientCode, patientId);
  }

  search({
    businessIdentifier,
    clientCode,
    firstName,
    lastName,
    birthName,
    birthDate,
    isArchived = null,
  }: {
    businessIdentifier: string;
    clientCode?: string;
    firstName?: string;
    lastName?: string;
    birthName?: string;
    birthDate?: Date;
    isArchived?: boolean;
  }): Observable<Patient[]> {
    return this.http.get<Patient[]>(`${environment.apiBaseUrl}/patient/`, {
      params: {
        ...(businessIdentifier && { businessIdentifier }),
        ...(firstName && { firstName }),
        ...(lastName && { lastName }),
        ...(birthName && { birthName }),
        ...(clientCode && { clientCode }),
        ...(birthDate && { birthDate: formatToDateOnly(birthDate) }),
        ...(isArchived !== null && { isArchived: String(isArchived) }),
        includeLocal: 'true',
      },
    });
  }

  updatePatient(clientCode: string, patientId: number, patient: Patient): Observable<Patient> {
    return this.http.patch<Patient>(`${environment.apiBaseUrl}/patient/${clientCode}/${patientId}`, {
      id: patient.id,
      businessIdentifier: patient.businessIdentifier,
      firstName: patient.firstName,
      lastName: patient.lastName,
      birthName: patient.birthName,
      birthDate: formatToDateOnly(patient.birthDate),
      isGamLinked: patient.isGamLinked,
    });
  }

  teleexpertise(
    clientCode: string,
    patientId: number,
    description: string,
    teleexpertiseProvider: TeleexpertiseProvider,
    reportRequest: PatientReportRequest,
  ): Observable<string> {
    return this.http.post(
      `${environment.apiBaseUrl}/patient/${patientId}/teleexpertise`,
      {
        clientCode,
        description,
        teleexpertiseProvider,
        ...reportRequest,
      },
      {
        responseType: 'text',
      },
    );
  }

  archive(clientCode: string, patientId: number): Observable<Patient> {
    return this.http.post<Patient>(`${environment.apiBaseUrl}/patient/${clientCode}/${patientId}/archive`, {}, {});
  }

  unarchive(clientCode: string, patientId: number): Observable<Patient> {
    return this.http.post<Patient>(`${environment.apiBaseUrl}/patient/${clientCode}/${patientId}/unarchive`, {}, {});
  }

  report(clientCode: string, patientId: number, patientReportRequest: PatientReportRequest): Observable<PdfResponse> {
    return this.http
      .post(`${environment.apiBaseUrl}/patient/${patientId}/report`, patientReportRequest, {
        headers: { 'X-version': '2.0' },
        params: {
          clientCode,
        },
        responseType: 'blob',
      })
      .pipe(
        map((blob: Blob) => ({
          content: blob,
          preview: this.sanitizer.bypassSecurityTrustResourceUrl(URL.createObjectURL(blob)),
        })),
      );
  }

  export(
    clientCode: string,
    patientId: number,
    encounterIdentifier: string,
    patientReportRequest: PatientReportRequest,
  ): Observable<string> {
    return this.http.post(`${environment.apiBaseUrl}/patient/${patientId}/export`, patientReportRequest, {
      headers: { 'X-version': '2.0' },
      params: {
        clientCode,
        encounterIdentifier,
      },
      responseType: 'text',
    });
  }
}
